/**
 * resolvePresetEntries — Resolves preset IDs and HDR names into
 * concrete PreloadEntry arrays.
 *
 * Pure function. No side effects. Deduplicates by URL so the same
 * asset (e.g. studio.hdr shared by spotlight, studio, hdri, transparent)
 * is only queued once.
 *
 * @see PreloadManager.preloadPreset()
 */

import type { PreloadEntry, PreloadPresetConfig } from '../api/types/preload';
import type { LoadingPhase } from '../api/types/loading';
import type { EnvironmentDescriptor } from '../api/types/environment';
import { ENVIRONMENT_DESCRIPTORS } from '../environment/EnvironmentDescriptors';
import { BUILT_IN_PRESET_DEFAULTS } from '../environment/PresetManager';

// ---------------------------------------------------------------------------
// resolvePresetEntries
// ---------------------------------------------------------------------------

/**
 * Resolve a `PreloadPresetConfig` into concrete `PreloadEntry[]`.
 *
 * @param config  High-level preset config (environment IDs and/or HDR names).
 * @param hdriBasePath  Base URL for HDR files (from EngineConfig.hdriBasePath).
 * @returns Deduplicated array of PreloadEntry objects ready for `preload()`.
 */
export function resolvePresetEntries(
  config: PreloadPresetConfig,
  hdriBasePath: string = '/assets/hdri',
): PreloadEntry[] {
  const phase: LoadingPhase = config.phase ?? 'eager';

  // Collect entries keyed by URL so we deduplicate automatically
  const byUrl = new Map<string, PreloadEntry>();

  // --- Process environment presets ---
  if (config.environments) {
    for (const envId of config.environments) {
      const descriptor = ENVIRONMENT_DESCRIPTORS[envId];
      const presetDefaults = BUILT_IN_PRESET_DEFAULTS[envId];
      if (!descriptor) continue;

      // 1. HDR from preset defaults
      if (presetDefaults?.ibl?.hdrFile) {
        const hdrUrl = `${hdriBasePath}/${presetDefaults.ibl.hdrFile}`;
        addEntry(byUrl, {
          id: `preset-hdr-${presetDefaults.ibl.hdrFile}`,
          url: hdrUrl,
          type: 'hdr',
          category: 'environments',
          phase,
        });
      }

      // 2. Visual assets from descriptor
      collectDescriptorAssets(byUrl, descriptor, phase);
    }
  }

  // --- Process standalone HDR files ---
  if (config.hdris) {
    for (const hdrFile of config.hdris) {
      const hdrUrl = `${hdriBasePath}/${hdrFile}`;
      addEntry(byUrl, {
        id: `preset-hdr-${hdrFile}`,
        url: hdrUrl,
        type: 'hdr',
        category: 'environments',
        phase,
      });
    }
  }

  return [...byUrl.values()];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract preloadable visual assets from an EnvironmentDescriptor.
 */
function collectDescriptorAssets(
  byUrl: Map<string, PreloadEntry>,
  descriptor: EnvironmentDescriptor,
  phase: LoadingPhase,
): void {
  const bg = descriptor.background;

  // Dome background — mesh + texture
  if (bg.type === 'dome') {
    if (bg.meshUrl) {
      addEntry(byUrl, {
        id: `preset-dome-mesh-${descriptor.id}`,
        url: bg.meshUrl,
        type: 'glb',
        category: 'environments',
        phase,
      });
    }
    if (bg.textureUrl) {
      addEntry(byUrl, {
        id: `preset-dome-tex-${descriptor.id}`,
        url: bg.textureUrl,
        type: 'texture',
        category: 'environments',
        phase,
      });
    }
  }

  // Ground textures
  const ground = descriptor.ground;
  if (ground?.visible !== false) {
    if (ground?.textureUrl) {
      addEntry(byUrl, {
        id: `preset-ground-tex-${descriptor.id}`,
        url: ground.textureUrl,
        type: 'texture',
        category: 'environments',
        phase,
      });
    }
    if (ground?.normalUrl) {
      addEntry(byUrl, {
        id: `preset-ground-nrm-${descriptor.id}`,
        url: ground.normalUrl,
        type: 'texture',
        category: 'environments',
        phase,
      });
    }
  }
}

/** Add an entry to the map, keyed by URL (first-write wins). */
function addEntry(
  byUrl: Map<string, PreloadEntry>,
  entry: PreloadEntry,
): void {
  if (!byUrl.has(entry.url)) {
    byUrl.set(entry.url, entry);
  }
}
